import type { FilaCartera } from '@/lib/supabase/tipos-bd'

/**
 * Las edades de la cartera, contadas desde que se finalizó el servicio.
 *
 * Viven aquí, en un solo sitio, porque las usan el resumen (la barra y su
 * leyenda, que además filtran) y cada tarjeta (la pastilla de «hace N
 * días»). Si un día cambian los tramos, cambian en todas partes a la vez.
 * El color va del más tranquilo al más urgente: morado de la marca, ámbar,
 * naranja, rojo.
 */
export const EDADES = [
  { clave: 'e0', rotulo: '0–30 días', desde: 0, hasta: 30 },
  { clave: 'e1', rotulo: '31–60 días', desde: 31, hasta: 60 },
  { clave: 'e2', rotulo: '61–90 días', desde: 61, hasta: 90 },
  { clave: 'e3', rotulo: 'Más de 90', desde: 91, hasta: Infinity },
] as const

export type ClaveEdad = (typeof EDADES)[number]['clave']

export const edadDe = (dias: number) =>
  EDADES.find((e) => dias >= e.desde && dias <= e.hasta) ?? EDADES[0]

/**
 * En qué paso de su vida va la cuenta. Johan pidió que la pantalla fuera
 * «más didáctica»: quien no lleva cartera tiene que entender, mirando una
 * tarjeta, de dónde viene la cuenta y qué le falta para cerrarse.
 */
export const PASOS = ['Finalizado', 'Facturada', 'Abonada', 'Pagada'] as const

/**
 * EN QUÉ VA EL COBRO. Desde la 0254 la cuenta vale sus FEV más sus COT sin
 * facturar (Johan: «en cartera sí puede haber COT, pero con alerta o rojo de
 * que se debe pasar a FEV»), así que una cuenta con COT se cobra igual; lo
 * que cambia es la alerta (debePasarAFev). «Sin documentos» es un servicio
 * finalizado que no tiene ni FEV ni COT: vale cero y no es «pagada» —saldo
 * cero no quiere decir que pagaron—.
 */
export type EstadoCuenta = 'sin_documentos' | 'por_cobrar' | 'abonada' | 'pagada'

export function estadoDe(c: FilaCartera): EstadoCuenta {
  if (Number(c.valor) <= 0) return 'sin_documentos'
  if (Number(c.saldo) <= 0) return 'pagada'
  if (Number(c.pagado) > 0) return 'abonada'
  return 'por_cobrar'
}

/** Tiene cotizaciones que todavía no son FEV: la alerta roja. */
export const debePasarAFev = (c: FilaCartera) => c.cot_sin_fev > 0

/**
 * El paso: con COT sin facturar no llega a «Facturada» hasta que pasen a
 * FEV, aunque ya le hayan abonado.
 */
export function pasoDe(c: FilaCartera): number {
  const e = estadoDe(c)
  if (e === 'pagada') return 3
  if (e === 'abonada') return 2
  return debePasarAFev(c) || e === 'sin_documentos' ? 0 : 1
}
